<template>
  <div class="generator-container">

    <!--StrengthAnimation :strength="strength"/-->

    <div class="generator">

      <div class="input-container">
        <div class="generator-input">
          <div class="password">{{ password }}</div>
          <span class="badge weak" v-if="strength === 0" v-bind:class="{ 'show-with-timeout': showAfterTimeout }">{{ texts.veryWeak }}</span>
          <span class="badge weak" v-else-if="strength === 1" v-bind:class="{ 'show-with-timeout': showAfterTimeout }">{{ texts.weak }}</span>
          <span class="badge mediocre" v-else-if="strength === 2" v-bind:class="{ 'show-with-timeout': showAfterTimeout }">{{ texts.mediocre }}</span>
          <span class="badge strong" v-else-if="strength === 3" v-bind:class="{ 'show-with-timeout': showAfterTimeout }">{{ texts.strong }}</span>
          <span class="badge veryStrong" v-else-if="strength === 4" v-bind:class="{ 'show-with-timeout': showAfterTimeout }">{{ texts.veryStrong }}</span>

          <div class="control-buttons" v-bind:class="{ 'show-with-timeout': showAfterTimeout }">
              <div class="generate-icon" v-bind:class="{ 'rotate': rotate }" @click="generatePasswordWithAnimation()">
              </div>
          </div>
        </div>
        <Button :caption="texts.button" classes="" v-bind:title="texts.copied" v-clipboard:copy="password" v-clipboard:success="onCopy" v-clipboard:error="onCopyError"/>
      </div>

      <div class="generator-settings" v-bind:class="{ 'show-with-timeout': showAfterTimeout }">
        <div class="length-settings">
          <label for="length" class="first text-x-large">{{ texts.passwordLength }}: </label>
          <span class="length text-large">{{ password_length }}</span>
          <button class="button-circle button-circle-md minus button-circle-secondary" v-bind:class="{ 'disabled': password_length === MIN_PASSWORD_LENGTH }" @click="decreaseLength">
            <img src="@/assets/images/minus-icon.svg" alt="">
          </button>
          <div class="slider-container">
            <input ref="slider" type="range" v-bind:min="MIN_PASSWORD_LENGTH" v-bind:max="MAX_PASSWORD_LENGTH" v-model="password_length" class="slider" @input="handleSliderInput()" @change="handleSliderChange()">
            <div ref="slider-border-part" id="slider-border-part" />
          </div>
          <button class="button-circle button-circle-md plus button-circle-secondary" v-bind:class="{ 'disabled': password_length === MAX_PASSWORD_LENGTH }" @click="increaseLength">
            <img src="@/assets/images/plus-icon.svg" alt="">
          </button>
        </div>

        <div class="complexity-settings">
          <label class="first text-x-large">{{ texts.charactersUsed }}: </label>
          <Checkbox id="uppercase" label="ABC" :value="complexity[0]" @valueChanged="handleChange" :disabled="disableChecked && complexity[0]"/>
          <Checkbox id="lowercase" label="abc" :value="complexity[1]" @valueChanged="handleChange" :disabled="disableChecked && complexity[1]"/>
          <Checkbox id="numbers" label="123" :value="complexity[2]" @valueChanged="handleChange" :disabled="disableChecked && complexity[2]"/>
          <Checkbox id="specials" label="#$&amp;" :value="complexity[3]" @valueChanged="handleChange" :disabled="disableChecked && complexity[3]"/>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import tippy from 'tippy.js'
import throttle from 'lodash.throttle'
import StrengthAnimation from './StrengthAnimation'
import Button from './Button.vue'
import Checkbox from './Checkbox.vue'
import { generate, check } from '../password-generator.js'

export default {
  name: 'PasswordGenerator',
  components: {
    StrengthAnimation,
    Button,
    Checkbox
  },
  props: ['showAfterTimeout'],
  data () {
    return {
      MIN_PASSWORD_LENGTH: 1,
      MAX_PASSWORD_LENGTH: 50,
      debounceTimer: 0,
      texts: window.passwordGeneratorTexts,

      firstGenerating: true,
      rotate: false,

      complexity: [true, true, true, false], // Uppercase, Lowercase, Numbers, Specials
      disableChecked: false,
      password_length: 15,

      password: '',
      strength: null,

      sliderProps: {
        background: '#008941'
      }
    }
  },
  mounted () {
    this.generatePasswordWithAnimation()

    this.$refs.slider.addEventListener('input', event => {
      this.applyFill(event.target, this.$refs['slider-border-part'])
    })

    this.applyFill(this.$refs.slider, this.$refs['slider-border-part'])
  },
  methods: {
    applyFill (slider, sliderBorderPart) {
      const percentage = (100 * (slider.value - slider.min)) / (slider.max - slider.min)
      const bg = `linear-gradient(90deg, transparent ${percentage}%, ${this.sliderProps.background} ${percentage + 0.1}%)`
      slider.style.background = bg
      sliderBorderPart.style.setProperty('--slider-border-part-width', `${percentage}%`)
    },
    generatePassword () {
      this.password = ''
      this.password = generate(this.password_length, this.complexity)
      this.strength = check(this.password)
    },
    generatePasswordWithAnimation: throttle(function () {
      this.password = ''
      this.handleRotateAnimation()
      this.animateGeneratingPassword(this.password_length, 1)
    }, 300),
    animateGeneratingPassword (finalLength, actualLength) {
      if (finalLength < 1) {
        this.strength = check(this.password)
        return
      }

      if (this.firstGenerating) {
        this.neverEndingGenerating(7)
      } else {
        setTimeout(function () {
          this.password = this.password.slice(0, actualLength - 1) + generate(finalLength, this.complexity)
          this.animateGeneratingPassword(--finalLength, ++actualLength)
        }.bind(this), 30)
      }
    },
    neverEndingGenerating (i) {
      if (i < 1) {
        this.firstGenerating = false
        this.animateGeneratingPassword(this.password_length, 1)
        return
      }
      setTimeout(function () {
        this.password = generate(this.password_length, this.complexity)
        this.neverEndingGenerating(--i)
      }.bind(this), 30)
    },
    increaseLength () {
      this.password_length++
      this.applyFill(this.$refs.slider, this.$refs['slider-border-part'])
      if (this.password_length > this.MAX_PASSWORD_LENGTH) {
        this.password_length = this.MAX_PASSWORD_LENGTH
        return
      }
      this.generatePassword()
    },
    decreaseLength () {
      this.password_length--
      this.applyFill(this.$refs.slider, this.$refs['slider-border-part'])
      if (this.password_length < this.MIN_PASSWORD_LENGTH) {
        this.password_length = this.MIN_PASSWORD_LENGTH
        return
      }
      this.generatePassword()
    },
    handleChange (value, id) {
      if (id === 'uppercase') {
        this.complexity[0] = value
      }
      if (id === 'lowercase') {
        this.complexity[1] = value
      }
      if (id === 'numbers') {
        this.complexity[2] = value
      }
      if (id === 'specials') {
        this.complexity[3] = value
      }
      this.preventUncheckAll()
      this.generatePasswordWithAnimation()
    },
    handleSliderChange () {
      if (this.isInternetExplorer() !== -1) {
        this.generatePassword()
      }
    },
    handleSliderInput () {
      this.generatePassword()
    },
    preventUncheckAll () {
      this.complexity.filter(item => item).length === 1 ? this.disableChecked = true : this.disableChecked = false
    },
    onCopy (event) {
      this.trackCopyButton()

      setTimeout(function () {
        tippy(event.trigger, {
          trigger: 'manual',
          arrow: true,
          placement: 'bottom'
        })
        event.trigger._tippy.show()
      }, 50)
      setTimeout(function () {
        event.trigger._tippy.hide()
      }, 1000)
    },
    onCopyError () {
      console.error('Failed to copy password.')
    },
    trackCopyButton () {
      try {
        window.dataLayer.push({
          'event': 'av.genericEvent',
          'eventCategory': 'CTA > Other > Password Generator',
          'eventAction': 'Copy generated password'
        })
      } catch (e) {
        console.error('Google Analytics not available')
      }
    },
    handleRotateAnimation () {
      this.rotate = true
      setTimeout(() => {
        this.rotate = false
      }, 550)
    },
    isInternetExplorer () {
      return window.navigator.userAgent.indexOf('Trident/') || window.navigator.userAgent.indexOf('MSIE ')
    }
  }
}
</script>

<style scoped>
div.generator-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-width: 940px;
}
div.generator-container .animation {
  width: 250px;
}
div.generator-container .generator {
  align-self: flex-end;
  margin-left: 32px;
}

div.generator-container .input-container {
  display: flex;
  align-items: center;
}

div.generator-container .generator-input {
  position: relative;
  display: flex;
  align-content: center;
  width: 535px;
  height: 54px;
  border: 1px solid #d4d2e6;
  background-color: #fff;
  box-sizing: content-box;
  margin-right: 16px;
  transition: border 0.3s;
}
div.generator-container .generator-input .password {
  width: 100%;
  text-align: left;
  font-size: 16px;
  line-height: 2.5;
  letter-spacing: 0.1px;
  color: #261c6a;
  padding: 10px 0px 10px 24px;
  margin: 0;
  border: none;
  outline: none;
  background-color: #fff;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  user-select: all;
  cursor: text;
}
div.generator-container .control-buttons {
  display: flex;
  align-content: center;
}
div.generator-container .generate-icon {
  min-width: 24px;
  height: 24px;
  margin: auto 22px;
  cursor: pointer;
  background: url('../assets/images/generate-icon.svg') no-repeat 50%;
}
div.generator-container .generate-icon.rotate {
  transition: all 0.5s ease-out;
  transform: rotate(-360deg);
}
div.generator-container .generate-icon path {
  transition: all 0.2s;
}
.badge {
  color: #fff;
  margin: auto 0;
  padding: 4px 16px;
  font-size: 11px;
  text-transform: uppercase;
  font-weight: 800;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  border-radius: 100px;
  white-space: nowrap;
}

.text-large {
  margin-bottom: 0;
}
div.generator-container .generator-settings {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  margin-top: 25px;
}
div.generator-container .length-settings, div.generator-container .complexity-settings {
  display: flex;
  align-items: center;
  box-sizing: content-box;
  color: #000;
}
div.generator-container .length-settings {
  margin-top: 25px;
  margin-bottom: 25px;
}
div.generator-container .complexity-settings {
  margin-top: 25px;
}
@media screen and (-ms-high-contrast: active), (-ms-high-contrast: none) {
  /* Only IE*/
  div.generator-container .length-settings {
    margin-top: 0px;
    margin-bottom: 0px;
  }
  div.generator-container .complexity-settings {
    margin-top: 0px;
  }
}
.length-settings .length {
  width: 16px;
  margin-right: 67px;
  color: #000;
  font-weight: 600;
}
.button-circle {
  border: 1px solid #008941;
  background-color: transparent;
  outline: none;
  border-radius: 4px;
  color: #008941;
}
.button-circle-secondary:hover {
  filter: none;
}
.button-circle-secondary:not(:disabled):not(.disabled):hover,
.button-circle-secondary:not(:disabled):not(.disabled):hover:active,
.button-circle-secondary:not(:disabled):not(.disabled):hover:focus,
.button-circle-secondary:not(:disabled):not(.disabled):focus,
.button-circle-secondary:not(:disabled):not(.disabled):active,
.button-circle-secondary:not(:disabled):not(.disabled):focus:active {
  border-color: #071D2B;
}
.button-circle img {
  filter: none;
}
.button-circle.minus {
  margin-right: 16px;
  padding: 16px;
}
.button-circle.plus {
  margin-left: 16px;
  padding: 16px 20px;
}
.button-circle.button-circle-md.minus img {
  width: 24px;
  height: auto;
}
.generator-settings label {
  color: #000;
  margin-bottom: 0;
}
.length-settings label.first {
  margin-left: 0px;
  margin-right: 20px;
}
.complexity-settings label.first {
  margin-left: 0px;
  margin-right: 107px;
}
.generator-settings label.last {
  margin-right: -5px;
}
.custom-control {
  margin-bottom: 0;
}
@supports (-ms-ime-align: auto) {
  .checkbox-label .checkmark:after {
    width: 16px;
    height: 16px;
    padding-top: 0px;
    padding-left: 1px;
  }
}
.slider-container {
  margin-top: -6px;
  position: relative;
}
/* Custom slider styles */
input[type=range] {
  -webkit-appearance: none;
  width: 285px;
  height: 12px;
  border-radius: 10px;
  cursor: pointer;
  position: relative;
  z-index: 1;
}
#slider-border-part {
  position: absolute;
  top: 6px;
  left: 0;
  width: var(--slider-border-part-width);
  height: 12px;
  background-color: #fff;
  border: 1px solid #000;
  border-radius: 10px;
}
@media screen and (-ms-high-contrast: active), (-ms-high-contrast: none) {
  /* Only IE*/
  input[type=range] {
    height: 45px;
  }
}
input[type=range]::-webkit-slider-runnable-track {
  width: 285px;
  height: 4px;
  border: none;
  border-radius: 2px;
  z-index: 100;
}
input[type=range]::-webkit-slider-thumb {
  -webkit-appearance: none;
  height: 48px;
  width: 48px;
  border: 1px solid #008941;
  border-radius: 50%;
  background: #fff url('../assets/images/slider-icon.svg') no-repeat center center;
  margin-top: -22px;
  cursor: pointer;
  z-index: 100;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.25);
}

input[type=range]:focus {
  outline: none;
}

input[type=range]::-moz-range-track {
  width: 285px;
  height: 4px;
  border: none;
  border-radius: 2px;
}
input[type=range]::-moz-range-thumb {
  height: 48px;
  width: 48px;
  border: 1px solid #008941;
  border-radius: 50%;
  background: #fff url('../assets/images/slider-icon.svg') no-repeat center center;
  cursor: pointer;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.25);
  z-index: 100;
}
input[type=range]::-moz-focus-outer  {
  border: 0;
}

input[type=range]::-ms-track {
  width: 285px;
  height: 4px;
  border-color: transparent;
  border-width: 6px 0;
  color: transparent;
}
@media screen and (-ms-high-contrast: active), (-ms-high-contrast: none) {
  /* Only IE*/
  input[type=range]::-ms-track {
    margin-top: 13px
  }
}
input[type=range]::-ms-fill-lower {
  background: #777;
  border-radius: 10px;
}
input[type=range]::-ms-fill-upper {
  background: #ddd;
  border-radius: 10px;
}
input[type=range]::-ms-thumb {
  border: none;
  height: 27px;
  width: 27px;
  border-radius: 50%;
  background: #fff url('../assets/images/slider-icon.svg') no-repeat center center;
  margin-top: -2px;
  cursor: pointer;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.25);
  z-index: 100;
}
input[type=range]:focus::-ms-fill-lower {
  background: #888;
}
input[type=range]:focus::-ms-fill-upper {
  background: #ccc;
}

span.veryStrong {
  background-color: #008941;
}

span.strong {
  background-color: #008941;
}

span.mediocre {
  background-color: #FFB600;
}

span.weak {
  background-color: #E46;
}

.tippy-tooltip .tippy-content {
  font-size: 15px;
  margin: 2px 10px 2px 10px
}

@media only screen and (max-width: 1024px) {
  div.generator-container {
    flex-direction: column;
    margin-bottom: 30px;
    min-width: 0;
  }

  div.generator-container .generator {
    margin-left: 0px;
  }

  div.generator-container .generator-input {
    width: 540px;
  }

  div.generator-container .generator-settings {
    flex-wrap: wrap;
    width: 553px;
  }

  div.generator-container .length-settings {
    margin-bottom: 20px;
  }

  /* input[type=range] {
    width: 250px;
  }

  input[type=range]::-moz-range-track {
    width: 250px;
  }

  input[type=range]::-ms-track {
    width: 250px;
  } */
}

@media only screen and (max-width: 991px) {
  /* #slider-border-part {
    top: 10px;
  } */
}

@media only screen and (max-width: 600px) {
  div.generator-container .input-container {
    flex-direction: column;
  }
  div.generator-container .generator-input {
    margin-bottom: 16px;
    margin-right: 0;
    width: 315px;
  }
  div.generator-container .generator-input input {
    width: 125px;
  }
  div.generator-container .generator-settings {
    flex-wrap: wrap;
    width: 326px;
  }
  div.generator-container .length-settings {
    flex-wrap: wrap;
    justify-content: center;
  }
  div.generator-container .length-settings .first{
    width: 326px;
    margin-bottom: 13px;
    margin-right: 0px;
  }
  div.generator-container .length{
    margin-bottom: 30px;
  }

  div.generator-container .length-settings {
    margin-bottom: 30px;
    text-align: center;
  }
  div.generator-container .complexity-settings {
    flex-wrap: wrap;
    justify-content: center;
  }
  div.generator-container .complexity-settings .first{
    width: 326px;
    margin-bottom: 13px;
    margin-right: 0px;
  }
  div.generator-container .generate-icon {
    margin: 10px 10px 0px 10px;
  }
  .checkbox-label {
    width: 40px;
  }
  .button-circle.minus, .button-circle.plus {
    display: none;
  }
  .length-settings .length {
    width: 100%;
    margin-right: 0px;
  }
  input[type=range] {
    /* width: 200px; */
  }
  input[type=range]::-moz-range-track {
    /* width: 200px; */
  }
  input[type=range]::-ms-track {
    /* width: 200px; */
  }
}

@media only screen and (max-width: 280px) {
  div.generator-container .generator-input {
    width: 260px;
  }
  input[type=range] {
    width: 150px;
  }
  input[type=range]::-moz-range-track {
    width: 150px;
  }
  input[type=range]::-ms-track {
    width: 150px;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/PasswordGenerator.vue